import { compileChart, type CompiledChart } from './ChartCompiler.js';
import { SongPackArchive, type ArchiveLimits } from './SongPackArchive.js';
import { fail } from './SongPackError.js';
import {
  ENTRY_SIZE_LIMITS,
  VIDEO_SIZE_LIMIT,
  decodeJson,
  expectsCover,
  parseChartDocument,
  parseManifest,
  validateCoverBytes,
  validateMusicBytes,
  validateVideoBytes,
} from './SongPackValidator.js';
import {
  DIFFICULTIES,
  chartFileName,
  type ChartDocumentV1,
  type Difficulty,
} from './schema/ChartDocumentV1.js';
import {
  COVER_FILE,
  MANIFEST_FILE,
  MUSIC_FILE,
  readMvFileReference,
  type ManifestV1,
} from './schema/ManifestV1.js';

/**
 * Read-only reader for Song Pack v1 archives.
 *
 * This module is the runtime's only contact point with the Song Pack format. It
 * never writes, never repairs and never rewrites a pack: a `.zip` produced by
 * Unity's `SongPackService` or by the Python chart engine is consumed exactly as
 * found.
 *
 * Deliberately free of `three`, DOM and Web Audio (requirements 13 and 14). That
 * is enforced by the compiler, not by convention: `tsconfig.json` builds `src/`
 * with `lib: ["ES2022"]` and `types: []`, so any DOM reference fails the build.
 * A `Blob` is accepted structurally, through its `arrayBuffer()` method, rather
 * than by depending on the DOM declaration of `Blob`.
 */

/** Anything the loader can read bytes from, including a DOM `Blob` or `File`. */
export type SongPackSource =
  | ArrayBuffer
  | ArrayBufferView
  | { arrayBuffer(): Promise<ArrayBuffer> };

export interface LoadOptions {
  /** Overrides for the archive-level structural limits. */
  readonly archiveLimits?: Partial<ArchiveLimits>;
  /**
   * Whether to inflate a bundled music video.
   *
   * Off by default, and that default is the important part: a video can be
   * hundreds of megabytes, and the *play* path never needs it in a
   * `Uint8Array` — it wants a `Blob` to hand to `createObjectURL`. Only import,
   * which has to move the bytes into storage once, asks for it.
   */
  readonly video?: 'skip' | 'include';
}

/** A bundled music video, as stored in the archive. */
export interface PackVideo {
  /** Entry name inside the archive, e.g. `video.mp4`. */
  readonly entry: string;
  readonly bytes: Uint8Array;
}

/** A validated Song Pack. Plain data, so it survives `structuredClone`. */
export interface LoadedSongPack {
  readonly manifest: ManifestV1;
  /** Raw `music.wav` bytes, still RIFF/WAVE encoded. */
  readonly music: Uint8Array;
  /** Raw `cover.png` bytes, or null when the manifest declares no cover. */
  readonly cover: Uint8Array | null;
  readonly charts: Readonly<Record<Difficulty, ChartDocumentV1>>;
  /**
   * The entry name of a bundled music video, or null when the pack carries none.
   *
   * Present whatever `LoadOptions.video` said — knowing *whether* a song travels
   * with its video costs nothing and is what the library index records. The bytes
   * are a separate question.
   */
  readonly videoEntry: string | null;
  /** Video bytes, only when `video: 'include'` was requested. */
  readonly video: PackVideo | null;
}

export const SongPackLoader = {
  /**
   * Reads a Song Pack from any byte source.
   *
   * @throws SongPackError with a specific `code` for every rejection.
   */
  async load(source: SongPackSource, options: LoadOptions = {}): Promise<LoadedSongPack> {
    return SongPackLoader.loadSync(await toBytes(source), options);
  },

  /** Synchronous variant, for callers that already hold the bytes. */
  loadSync(data: Uint8Array | ArrayBuffer, options: LoadOptions = {}): LoadedSongPack {
    const bytes = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
    const archive = SongPackArchive.open(bytes, options.archiveLimits ?? {});

    // manifest.json first: it declares whether a cover is expected, and an
    // unknown formatVersion must stop interpretation before anything else is
    // read. (Unity reads music.wav before validating the version; rejecting the
    // version earlier is the safer order and produces the same accept/reject set.)
    const manifestEntry = archive.extract([MANIFEST_FILE], ENTRY_SIZE_LIMITS);
    const manifest = parseManifest(
      decodeJson(mustGet(manifestEntry, MANIFEST_FILE), MANIFEST_FILE),
    );

    const wanted = [MUSIC_FILE, ...DIFFICULTIES.map(chartFileName)];
    if (expectsCover(manifest)) wanted.push(COVER_FILE);
    const payload = archive.extract(wanted, ENTRY_SIZE_LIMITS);

    const music = mustGet(payload, MUSIC_FILE);
    validateMusicBytes(music);

    let cover: Uint8Array | null = null;
    if (expectsCover(manifest)) {
      cover = mustGet(payload, COVER_FILE);
      validateCoverBytes(cover);
    }

    const charts = {} as Record<Difficulty, ChartDocumentV1>;
    for (const difficulty of DIFFICULTIES) {
      const file = chartFileName(difficulty);
      charts[difficulty] = parseChartDocument(
        decodeJson(mustGet(payload, file), file),
        difficulty,
        file,
      );
    }

    // A video is a bundled asset only when the manifest names an entry the
    // archive actually holds. A reference to anything else is an *external* one
    // and stays the Mirror Drift resolver's business, not the reader's.
    const reference = readMvFileReference(manifest);
    const videoEntry =
      reference !== undefined && archive.has(reference) ? archive.info(reference)!.name : null;

    // A bundled video is always *checked*, whatever the caller asked for: a file
    // that is not a video at all should be refused at the door, not stored and
    // discovered later as a silent "Mirror Drift is off".
    //
    // How much that costs depends on how the entry was written. Stored — which
    // is how this runtime writes one, an MP4 being already compressed — needs
    // sixteen bytes. Deflated leaves no way to see the header without inflating
    // the whole thing, so those bytes are kept once they have been paid for,
    // even when the caller said `skip`.
    let video: PackVideo | null = null;
    if (videoEntry !== null) {
      const range = archive.storedRange(videoEntry);
      if (range !== null) {
        validateVideoBytes(bytes.subarray(range.start, Math.min(range.start + 16, range.end)), videoEntry);
        if (options.video === 'include') {
          video = { entry: videoEntry, bytes: bytes.subarray(range.start, range.end) };
        }
      } else {
        const inflated = mustGet(
          archive.extract([videoEntry], { [videoEntry]: VIDEO_SIZE_LIMIT }),
          videoEntry,
        );
        validateVideoBytes(inflated, videoEntry);
        video = { entry: videoEntry, bytes: inflated };
      }
    }

    return { manifest, music, cover, charts, videoEntry, video };
  },

  /** Reads a pack and compiles a single difficulty in one step. */
  async loadCompiled(
    source: SongPackSource,
    difficulty: Difficulty,
    options: LoadOptions = {},
  ): Promise<{ pack: LoadedSongPack; chart: CompiledChart }> {
    const pack = await SongPackLoader.load(source, options);
    return { pack, chart: compileSongPackChart(pack, difficulty) };
  },
} as const;

/** The bytes of each entry, however the caller came by them. */
export interface SongPackParts {
  readonly manifest: Uint8Array;
  readonly easy: Uint8Array;
  readonly normal: Uint8Array;
  readonly hard: Uint8Array;
  readonly music: Uint8Array;
  readonly cover?: Uint8Array | null;
  /** Entry name of a bundled video the caller holds elsewhere, e.g. as a Blob. */
  readonly videoEntry?: string | null;
}

/**
 * Validates a pack that has already been taken out of its archive.
 *
 * The route a stored library takes. Every rule `loadSync` applies is applied
 * here too, by the same functions — assets that have been sitting in a database
 * are no more trustworthy than a file someone just handed over, and a pack that
 * has been through storage should fail for exactly the reasons it would have
 * failed on the way in.
 *
 * What is *not* re-checked is the ZIP container, because there isn't one.
 */
export function loadSongPackParts(parts: SongPackParts): LoadedSongPack {
  const manifest = parseManifest(decodeJson(parts.manifest, MANIFEST_FILE));
  validateMusicBytes(parts.music);

  const cover = parts.cover ?? null;
  if (expectsCover(manifest)) {
    if (cover === null) {
      fail('MISSING_ENTRY', `Missing Song Pack entry: ${COVER_FILE}`, { entry: COVER_FILE });
    }
    validateCoverBytes(cover);
  }

  const charts = {} as Record<Difficulty, ChartDocumentV1>;
  for (const difficulty of DIFFICULTIES) {
    const file = chartFileName(difficulty);
    charts[difficulty] = parseChartDocument(decodeJson(parts[difficulty], file), difficulty, file);
  }

  return {
    manifest,
    music: parts.music,
    // A cover the manifest does not declare is ignored rather than adopted:
    // `coverFile` is what every reader branches on.
    cover: expectsCover(manifest) ? cover : null,
    charts,
    videoEntry: parts.videoEntry ?? null,
    video: null,
  };
}

/**
 * The `.chart` documents of an archive, exactly as JSON, unparsed.
 *
 * Deliberately not `ChartDocumentV1`: a regeneration hands these back to the
 * engine so it can copy the notes it is keeping, and a document that has been
 * through the reader's shape is a document whose absent fields have been filled
 * in with defaults and whose unknown ones have been dropped. What travels back
 * has to be what the pack holds, not the runtime's reading of it.
 *
 * The archive is opened under the same limits as a load, so this is no more of
 * a door into the process than reading the pack normally is.
 */
export function readRawChartDocuments(
  data: Uint8Array | ArrayBuffer,
  options: LoadOptions = {},
): Record<Difficulty, unknown> {
  const bytes = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
  const archive = SongPackArchive.open(bytes, options.archiveLimits ?? {});
  const payload = archive.extract(DIFFICULTIES.map(chartFileName), ENTRY_SIZE_LIMITS);
  const out = {} as Record<Difficulty, unknown>;
  for (const difficulty of DIFFICULTIES) {
    const file = chartFileName(difficulty);
    out[difficulty] = decodeJson(mustGet(payload, file), file);
  }
  return out;
}

/** Selects a difficulty's `.chart` document. */
export function getChartDocument(
  pack: LoadedSongPack,
  difficulty: Difficulty,
): ChartDocumentV1 {
  const chart = pack.charts[difficulty];
  if (chart === undefined) {
    fail('MISSING_ENTRY', `The pack has no ${difficulty} chart.`, {
      entry: chartFileName(difficulty),
    });
  }
  return chart;
}

/** Selects a difficulty and compiles it into the runtime representation. */
export function compileSongPackChart(
  pack: LoadedSongPack,
  difficulty: Difficulty,
): CompiledChart {
  return compileChart(getChartDocument(pack, difficulty));
}

/**
 * Returns `music.wav` as a standalone `ArrayBuffer`.
 *
 * A fresh copy is made on every call by design: `decodeAudioData` detaches the
 * buffer it is given, so handing out the loader's own storage would destroy the
 * pack on first decode.
 */
export function copyMusicArrayBuffer(pack: LoadedSongPack): ArrayBuffer {
  return copyBytes(pack.music);
}

/** Returns `cover.png` as a standalone `ArrayBuffer`, or null when absent. */
export function copyCoverArrayBuffer(pack: LoadedSongPack): ArrayBuffer | null {
  return pack.cover === null ? null : copyBytes(pack.cover);
}

// ---------------------------------------------------------------------------

async function toBytes(source: SongPackSource): Promise<Uint8Array> {
  if (source instanceof ArrayBuffer) return new Uint8Array(source);
  if (ArrayBuffer.isView(source)) {
    return new Uint8Array(source.buffer, source.byteOffset, source.byteLength);
  }
  if (typeof source === 'object' && typeof source.arrayBuffer === 'function') {
    return new Uint8Array(await source.arrayBuffer());
  }
  fail('INVALID_ARCHIVE', 'Unsupported Song Pack source; expected ArrayBuffer, view or Blob.');
}

function copyBytes(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function mustGet(entries: Map<string, Uint8Array>, name: string): Uint8Array {
  const bytes = entries.get(name);
  if (bytes === undefined) {
    fail('MISSING_ENTRY', `Missing Song Pack entry: ${name}`, { entry: name });
  }
  return bytes;
}
